import { useCallback, useState } from 'react';

import { useAuthStore } from '@/shared/store/authStore';
import { extractErrorMessage } from '@/shared/utils/errorUtils';

import { LoginFormData } from '../types/authTypes';

interface UseAuthReturn {
  loading: boolean;
  error: string | null;
  login: (
    loginData: LoginFormData,
  ) => Promise<{ success: boolean; message?: string }>;
  clearError: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authLogin = useAuthStore((state) => state.login);

  const login = useCallback(
    async (loginData: LoginFormData) => {
      try {
        setLoading(true);
        setError(null);

        const result = await authLogin(loginData);

        if (!result.success) {
          setError(result.message || 'Failed to login. Please try again.');
        }

        return result;
      } catch (err) {
        const errorMessage = extractErrorMessage(err);
        setError(errorMessage);
        return { success: false, message: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [authLogin],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    login,
    clearError,
  };
};
